import type { ReactNode, RefObject } from "react";
import { ModalSurface } from "./ModalSurface";
import { useControllableState } from "../utils/useControllableState";

export interface OverlayTriggerProps {
  "aria-haspopup": "dialog";
  "aria-expanded": boolean;
  onClick: () => void;
}

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  trigger?: (props: OverlayTriggerProps) => ReactNode;
  closeLabel?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
  className?: string;
}

export function Dialog({
  open,
  defaultOpen = false,
  onOpenChange,
  title,
  description,
  children,
  footer,
  trigger,
  closeLabel,
  initialFocusRef,
  className,
}: DialogProps) {
  const [isOpen, setIsOpen] = useControllableState({
    value: open,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  });

  return (
    <>
      {trigger?.({
        "aria-haspopup": "dialog",
        "aria-expanded": isOpen,
        onClick: () => setIsOpen(true),
      })}
      <ModalSurface
        open={isOpen}
        title={title}
        description={description}
        footer={footer}
        onClose={() => setIsOpen(false)}
        closeLabel={closeLabel}
        initialFocusRef={initialFocusRef}
        variant="dialog"
        className={className}
      >
        {children}
      </ModalSurface>
    </>
  );
}
